/**
 * GoodGuardian — Messages / Notification Inbox
 * Polls every 15s when tab is visible. Shows system, co-parent, admin messages.
 */
import { useEffect, useState, useCallback } from 'react'
import AppHeader from '@/components/layout/AppHeader'
import { useApp } from '@/context/AppContext'
import { useNavigate } from 'react-router-dom'

const CATEGORY_CONFIG = {
  system:   { bg: 'bg-blue-50',   border: 'border-blue-100',   icon: '🛡️', label: 'System'    },
  coparent: { bg: 'bg-purple-50', border: 'border-purple-100', icon: '👨‍👩‍👧', label: 'Co-Parent' },
  admin:    { bg: 'bg-amber-50',  border: 'border-amber-100',  icon: '📢', label: 'Admin'     },
}

function MessageCard({ msg, onRead, onDelete, onNavigate }) {
  const cfg    = CATEGORY_CONFIG[msg.category] || CATEGORY_CONFIG.system
  const date   = new Date(msg.created_at)
  const isToday = new Date().toDateString() === date.toDateString()
  const timeStr = isToday
    ? date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

  const handleTap = () => {
    if (!msg.read) onRead(msg.id)
    if (msg.action_url) onNavigate(msg.action_url)
  }

  return (
    <div
      className={[
        'rounded-2xl border p-4 transition-all',
        cfg.bg, cfg.border,
        !msg.read ? 'shadow-sm' : 'opacity-75',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <span className="text-xl flex-shrink-0 mt-0.5">{cfg.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                {cfg.label}
              </span>
              {!msg.read && (
                <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
              )}
            </div>
            <span className="text-xs text-slate-500 flex-shrink-0">{timeStr}</span>
          </div>
          <p className="text-sm font-semibold text-slate-800 mb-1">{msg.subject}</p>
          <p className="text-sm text-slate-600 leading-relaxed">{msg.body}</p>
          {msg.from_name && (
            <p className="text-xs text-slate-500 mt-1">From: {msg.from_name}</p>
          )}
          <div className="flex items-center gap-3 mt-3">
            {msg.action_url && (
              <button
                onClick={handleTap}
                className="text-xs text-blue-600 font-semibold"
              >
                View →
              </button>
            )}
            {!msg.read && (
              <button
                onClick={() => onRead(msg.id)}
                className="text-xs text-slate-500"
              >
                Mark read
              </button>
            )}
            <button
              onClick={() => onDelete(msg.id)}
              className="text-xs text-red-400 ml-auto"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Messages() {
  const navigate              = useNavigate()
  const { user }              = useApp()
  const [messages, setMessages] = useState([])
  const [unread, setUnread]   = useState(0)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter]   = useState('all')

  const fetchMessages = useCallback(async () => {
    try {
      const token = localStorage.getItem('cg_token')
      const res   = await fetch('/api/data/messages', {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (!res.ok) return
      const data = await res.json()
      setMessages(data.messages || [])
      setUnread(data.unread || 0)
    } catch {}
    finally { setLoading(false) }
  }, [])

  // Initial load + 15s polling when tab is visible
  useEffect(() => {
    fetchMessages()
    const interval = setInterval(() => {
      if (!document.hidden) fetchMessages()
    }, 15000)
    return () => clearInterval(interval)
  }, [fetchMessages])

  const markRead = async (id) => {
    const token = localStorage.getItem('cg_token')
    await fetch(`/api/data/messages?id=${id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` },
    })
    setMessages(prev => prev.map(m => m.id === id ? { ...m, read: 1 } : m))
    setUnread(prev => Math.max(0, prev - 1))
  }

  const markAllRead = async () => {
    const token = localStorage.getItem('cg_token')
    await fetch('/api/data/messages', {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` },
    })
    setMessages(prev => prev.map(m => ({ ...m, read: 1 })))
    setUnread(0)
  }

  const deleteMsg = async (id) => {
    const token = localStorage.getItem('cg_token')
    await fetch(`/api/data/messages?id=${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    })
    setMessages(prev => prev.filter(m => m.id !== id))
  }

  const handleNavigate = (url) => {
    navigate(url)
  }

  const filtered = filter === 'all'
    ? messages
    : filter === 'unread'
      ? messages.filter(m => !m.read)
      : messages.filter(m => m.category === filter)

  // Group by date
  const groups = filtered.reduce((acc, msg) => {
    const d   = new Date(msg.created_at)
    const key = new Date().toDateString() === d.toDateString()
      ? 'Today'
      : new Date(Date.now() - 86400000).toDateString() === d.toDateString()
        ? 'Yesterday'
        : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
    if (!acc[key]) acc[key] = []
    acc[key].push(msg)
    return acc
  }, {})

  return (
    <div className="flex flex-col h-full bg-white">
      <AppHeader title="Messages" />
      <div className="screen no-scrollbar px-4 py-4 space-y-4">

        {/* Unread badge + mark all */}
        {unread > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-600">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold mr-1.5">
                {unread > 9 ? '9+' : unread}
              </span>
              unread
            </span>
            <button onClick={markAllRead} className="text-sm text-blue-600 font-medium">
              Mark all read
            </button>
          </div>
        )}

        {/* Filter pills */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {[['all','All'],['unread','Unread'],['coparent','Co-Parent'],['system','System'],['admin','Admin']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap flex-shrink-0 transition-colors ${
                filter === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Messages */}
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="w-7 h-7 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : Object.keys(groups).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <p className="text-slate-600 font-semibold">No messages</p>
            <p className="text-slate-500 text-sm mt-1">
              {filter === 'unread' ? 'All caught up!' : 'Notifications and alerts will appear here.'}
            </p>
          </div>
        ) : (
          Object.entries(groups).map(([dateLabel, msgs]) => (
            <div key={dateLabel} className="space-y-2">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest px-1">{dateLabel}</p>
              {msgs.map(msg => (
                <MessageCard
                  key={msg.id}
                  msg={msg}
                  onRead={markRead}
                  onDelete={deleteMsg}
                  onNavigate={handleNavigate}
                />
              ))}
            </div>
          ))
        )}

        {/* Real-time note */}
        <p className="text-center text-xs text-slate-300 pb-2">
          Refreshes every 15 seconds
        </p>
      </div>
    </div>
  )
}
